import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import ScrollToTop from './components/ScrollToTop';
import ComingSoon from './components/ComingSoon';
import Layout from './components/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { showConsoleEasterEgg } from './utils/consoleEasterEgg';

// Lazy load pages for better performance
const Home = React.lazy(() => import('./pages/Home'));
const Services = React.lazy(() => import('./pages/Services'));
const Technologies = React.lazy(() => import('./pages/Technologies'));
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
// Component with language provider (defaults to Polish)
const AppContent: React.FC = () => {
  return (
    <Router>
      <LanguageProvider>
        <Suspense fallback={<LoadingSpinner fullScreen />}>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Polish routes (default) */}
              <Route path="/" element={<Home />} />
              <Route path="/uslugi" element={<Services />} />
              <Route path="/technologie" element={<Technologies />} />
              <Route path="/o-mnie" element={<About />} />
              <Route path="/kontakt" element={<Contact />} />

              {/* English routes (/en/ subfolder) */}
              <Route path="/en" element={<Home />} />
              <Route path="/en/services" element={<Services />} />
              <Route path="/en/technologies" element={<Technologies />} />
              <Route path="/en/about" element={<About />} />
              <Route path="/en/contact" element={<Contact />} />

              {/* Legacy English routes redirect to /en/ */}
              <Route path="/services" element={<Navigate to="/en/services" replace />} />
              <Route path="/technologies" element={<Navigate to="/en/technologies" replace />} />
              <Route path="/about" element={<Navigate to="/en/about" replace />} />
              <Route path="/contact" element={<Navigate to="/en/contact" replace />} />
            </Routes>
          </Layout>
        </Suspense>
      </LanguageProvider>
    </Router>
  );
};

function App() {
  const isComingSoonEnabled = import.meta.env.VITE_COMING_SOON_ENABLED === 'true';

  // Show console easter egg when app loads
  useEffect(() => {
    // Small delay to ensure page is loaded
    const timer = setTimeout(() => {
      showConsoleEasterEgg();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (isComingSoonEnabled) {
    return <ComingSoon />;
  }

  // Main application with i18n routing
  return <AppContent />;
}

export default App;
